import { Input } from '@app/components/various/Fields/Input';
import { Skeleton } from '@app/components/various/Skeleton';
import { getCmsNavigationItems, getHasCmsNavigationFailed } from '@app/ducks';
import { flattenNavigationItems } from '@app/logic/navigation';
import { useTranslations } from '@app/services/hooks/translations/useTranslations';
import { useTranslationsConfig } from '@app/utils/hooks';
import { useTable } from '@app/utils/hooks/table/useTable';
import { isDefined } from '@app/utils/is';
import { isEmpty } from '@app/utils/isEmpty';
import { createColumnHelper, flexRender, getCoreRowModel, getSortedRowModel } from '@tanstack/react-table';
import { Navigation, Translations } from '@typings';
import cx from 'classnames';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { NavigationItemName } from './NavigationItemName';

interface Props {
  isLoading: boolean;
}

interface TableRow extends Partial<Translations.AllLanguagesTranslations> {
  id: string;
  type: Navigation.AnyMenuItemType;
}

const columnHelper = createColumnHelper<TableRow>();

export const NavigationItemsTable = ({ isLoading }: Props) => {
  const { t } = useTranslation(['common', 'translations']);
  const { additionalLanguages } = useTranslationsConfig();
  const navigationItems = useSelector(getCmsNavigationItems);
  const hasCMSFetchNavigationFailed = useSelector(getHasCmsNavigationFailed);

  const flattenedNavigationItems = React.useMemo(() => flattenNavigationItems(navigationItems), [navigationItems]);
  const hasNavigationItems = !isEmpty(navigationItems);

  const { data: translations } = useTranslations();
  const { register } = useFormContext<Translations.TranslationsDTOAdditional>();

  const getItemTranslations = React.useCallback(
    ({ id, type }: Navigation.AnyMenuItem) => {
      if (!isDefined(translations)) {
        return null;
      }

      const translationsForId = translations.navigationItems[id];

      return {
        id,
        type,
        ...translationsForId,
      };
    },
    [translations],
  );

  const columns = React.useMemo(
    () => [
      columnHelper.accessor('en', {
        cell: info => <NavigationItemName name={info.getValue() ?? ''} type={info.row.original.type} />,
        header: t('common:system_field'),
      }),
      ...additionalLanguages.map(({ code, label }) =>
        columnHelper.accessor(code, {
          cell: info => {
            const { en, id } = info.row.original;

            return (
              <label aria-label={t('translations:translation_for', { code, label: en })}>
                <Input {...register(`navigationItems.${id}.${code}`)} placeholder={en} />
              </label>
            );
          },
          enableSorting: false,
          header: label,
        }),
      ),
    ],
    [t, additionalLanguages, register],
  );

  const dataSource = React.useMemo(
    () => flattenedNavigationItems.map(getItemTranslations).filter(isDefined),
    [flattenedNavigationItems, getItemTranslations],
  );

  const table = useTable({
    columns,
    data: dataSource,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  if (hasCMSFetchNavigationFailed) {
    return null;
  }

  const tableRows = table.getRowModel().rows;

  return (
    <section className="box space-y-5">
      <h3 className="heading-3">{t('translations:navigation_menu_items_title')}</h3>
      <p className="text-neutral-40 body-2">{t('translations:navigation_menu_items_subtitle')}</p>
      {!hasNavigationItems && !isLoading && <span className="text-neutral-40 body-2">{t('translations:no_navigation_items')}</span>}
      {(hasNavigationItems || isLoading) && (
        <div className="overflow-auto">
          <table className="border-separate">
            <colgroup>
              {table
                .getAllLeafColumns()
                .filter(column => column.getIsVisible())
                .map((_, index) => (
                  <col key={index} style={{ minWidth: index === 0 ? '240px' : '220px' }} />
                ))}
            </colgroup>
            <thead>
              {table.getHeaderGroups().map(headerGroup => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map(columnHeader => (
                    <th key={columnHeader.id} className="py-1 pl-5 text-left body-1 first:pl-0">
                      {flexRender(columnHeader.column.columnDef.header, columnHeader.getContext())}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {tableRows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {row.getVisibleCells().map(cell => (
                    <td key={cell.id} className={cx('pl-5 first:pl-0', { 'pb-5': tableRows.length - 1 !== rowIndex })}>
                      {isLoading ?
                        <Skeleton type="input" />
                      : flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
              {isEmpty(tableRows) &&
                isLoading &&
                Array.from({ length: 10 }).map((_, rowIndex) => (
                  <tr key={rowIndex}>
                    {table.getAllLeafColumns().map(column => (
                      <td key={column.id} className={cx('pl-5 first:pl-0', { 'pb-5': tableRows.length - 1 !== rowIndex })}>
                        <Skeleton type="input" />
                      </td>
                    ))}
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};
